import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, Button, Popover, Skeleton, Tooltip, Typography } from "@mui/material";
import { RichTreeView, TreeItem } from "@mui/x-tree-view";
import { truncate } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { usePopover } from "../../../utils/hooks/use-popover";
import { useFolders } from "../hooks/use-folders";
import { useUpsertAssetCallback } from "../hooks/use-upsert-asset";
import { ROOT_ID, folderNameFromId, folderParentMap, transformFoldersToTreeItems } from "../utils";

import type { UUID } from "../../../utils/uuid";
import type { Asset } from "../server/digital-asset-service";
import type { FolderId, UiFolderId } from "../utils";
import type { TreeItemProps } from "@mui/x-tree-view";
import type { FC, ReactElement } from "react";

export const AssetFolder: FC<{ asset: Asset; onFolderChange: () => void }> = ({
  asset,
  onFolderChange,
}) => {
  const { data: folders, loading, error } = useFolders();
  const upsertAsset = useUpsertAssetCallback();
  const [anchorRef, menuController] = usePopover<HTMLButtonElement>();
  const initialFolderId = asset.folder_id ?? ROOT_ID;
  const [currentFolderId, setCurrentFolderId] = useState<UiFolderId>(initialFolderId);
  const [selectedFolderId, setSelectedFolderId] = useState<UiFolderId>(initialFolderId);
  const [expandedItems, setExpandedItems] = useState<string[]>([ROOT_ID]);
  const [moving, setMoving] = useState(false);
  const previousOpen = useRef(false);

  const folderMap = useMemo(() => folderParentMap(folders), [folders]);

  const getExpandedPath = useCallback(
    (targetId: UiFolderId): string[] => {
      const expanded = [ROOT_ID];
      if (!targetId || targetId === ROOT_ID) return expanded;

      let currentId: FolderId = targetId;
      while (currentId) {
        const parentId = folderMap.get(currentId);
        if (!parentId) break;
        expanded.push(parentId);
        currentId = parentId;
      }

      return expanded;
    },
    [folderMap],
  );

  useEffect(() => {
    const wasOpen = previousOpen.current;
    previousOpen.current = menuController.open;
    if (!menuController.open || wasOpen || error) return;
    setExpandedItems(getExpandedPath(selectedFolderId));
  }, [error, getExpandedPath, menuController.open, selectedFolderId]);

  const hasPendingMove = selectedFolderId !== currentFolderId;

  const handleConfirmMove = async (): Promise<void> => {
    if (!hasPendingMove || moving) return;
    setMoving(true);

    try {
      const nextFolderId = selectedFolderId === ROOT_ID ? null : selectedFolderId;
      const result = await upsertAsset({ ...asset, folder_id: nextFolderId });
      if (result?.success) {
        setCurrentFolderId(selectedFolderId);
        const name = folderNameFromId(selectedFolderId, folders);
        toast.success(`Asset moved to '${name}' folder`);
        onFolderChange();
      } else {
        toast.error("Asset folder move failed");
      }
    } finally {
      setMoving(false);
    }
  };

  const folderName = folderNameFromId(currentFolderId, folders) ?? "Unknown folder";

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <FolderOutlined fontSize="small" sx={{ color: "text.secondary" }} />
          {loading ? (
            <Skeleton variant="text" width={160} />
          ) : (
            <Tooltip title={folderName.length > 36 ? folderName : ""}>
              <Typography variant="body2" noWrap>
                {truncate(folderName, { length: 36 })}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Button
          ref={anchorRef}
          onClick={menuController.handleOpen}
          variant="text"
          size="small"
          sx={{ textTransform: "none", fontWeight: 500, px: 0, whiteSpace: "nowrap" }}
          data-analytics-id="asset-folder-move-open-popover"
        >
          Move to different folder
        </Button>
      </Box>
      <Popover
        open={menuController.open}
        onClose={() => {
          setSelectedFolderId(currentFolderId);
          menuController.handleClose();
        }}
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              width: 340,
              maxHeight: 360,
              overflowY: "auto",
            },
          },
        }}
      >
        {loading && (
          <Box px={1} py={0.5}>
            <Skeleton variant="text" width={200} />
            <Skeleton variant="text" width={180} />
            <Skeleton variant="text" width={160} />
          </Box>
        )}
        {!loading && error && (
          <Box px={1} py={0.5}>
            <Typography variant="body2" color="error">
              Unable to load folders
            </Typography>
          </Box>
        )}
        {!loading && !error && (
          <RichTreeView
            items={transformFoldersToTreeItems(folders ?? [])}
            itemChildrenIndentation={8}
            selectedItems={selectedFolderId}
            expandedItems={expandedItems}
            onExpandedItemsChange={(_, itemIds) => setExpandedItems(itemIds)}
            onItemClick={(_, itemId) => {
              // oxlint-disable-next-line typescript/no-unsafe-type-assertion
              setSelectedFolderId(itemId === ROOT_ID ? itemId : (itemId as UUID));
            }}
            slots={{
              // oxlint-disable-next-line react/no-unstable-nested-components -- Uses parent state; making it stable changes remount behaviour, handle separately
              item: (props) => (
                <AssetFolderTreeItem
                  {...props}
                  hasPendingMove={hasPendingMove}
                  onConfirmMove={handleConfirmMove}
                  selectedFolderId={selectedFolderId}
                  moving={moving}
                />
              ),
            }}
            expansionTrigger="iconContainer"
            sx={{
              "& .MuiTreeItem-content": {
                pr: 1,
                pl: "calc(8px + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))",
                py: 0.5,
                borderRadius: 1,
              },
              "& .MuiTreeItem-label": {
                width: "100%",
              },
              "& .MuiTreeItem-group": {
                ml: 2,
              },
            }}
          />
        )}
      </Popover>
    </>
  );
};

const AssetFolderTreeItem = (
  props: TreeItemProps & {
    hasPendingMove: boolean;
    onConfirmMove: () => Promise<void>;
    selectedFolderId: FolderId;
    moving: boolean;
  },
): ReactElement => {
  const { itemId, label, hasPendingMove, onConfirmMove, selectedFolderId, moving, ...rest } = props;
  const labelText = typeof label === "string" ? label : "";
  const isRoot = itemId === ROOT_ID;
  const isSelected = itemId === selectedFolderId;
  const showMoveButton = isSelected && hasPendingMove;

  return (
    <TreeItem
      {...rest}
      itemId={itemId}
      label={
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
            {isRoot ? <PermMediaOutlined fontSize="small" /> : <FolderOutlined fontSize="small" />}
            <Typography variant="body2" noWrap title={labelText} sx={{ minWidth: 0 }}>
              {truncate(labelText, { length: 16 })}
            </Typography>
          </Box>
          {showMoveButton && (
            <Tooltip title="Move asset to this folder">
              <span>
                <Button
                  disabled={moving}
                  size="small"
                  variant="text"
                  onClick={(event) => {
                    event.stopPropagation();
                    void onConfirmMove();
                  }}
                  data-analytics-id="asset-folder-move-confirm"
                >
                  Move here
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      }
    />
  );
};
